import React, { useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "../../../store";
import {
  SelectChangeEvent,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Box as MuiBox,
} from "@mui/material";
import Select, { SelectProps } from "../../../components/Select";
import { TextField, Autocomplete } from "@mui/material";
import {
  SecondContainer,
  SecondBox,
  ThirdBox,
  Box,
  Typography,
} from "../styles";
import {
  getLanguageCode,
  defaultAllCode,
} from "../../Templates/LanguageCode";
import {
  defaultAllCategory,
  getCategoryCode,
} from "../../Templates/CategoryCode";
import DateRangePicker from "../../../components/DateRangePicker";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import dayjs from "dayjs";
import { useCookies, Cookies } from "react-cookie";
import moment from "moment";

/**
 * 필터 요약 줄의 "라벨 + 값" 한 쌍. (Templates / Moderation 요약 줄과 같은 구조)
 *
 * 라벨과 값을 하나의 요소로 묶는 게 핵심이다. 예전처럼 형제 <p> 로 나열하면
 * 요약 줄에 걸린 flex-wrap 이 라벨과 값 사이에서 줄을 끊어, 라벨만 윗줄에
 * 남고 값이 다음 줄로 넘어가는 일이 생겼다.
 */
const FilterItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <MuiBox
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      mr: "16px",
      whiteSpace: "nowrap",
    }}
  >
    <Typography sx={{ color: "#79747E" }}>{label}</Typography>
    <Typography sx={{ display: "inline-flex", alignItems: "center" }}>
      {children}
    </Typography>
  </MuiBox>
);

const Handler = () => {
  const {
    AIReviewStore: {
      gridOption: { rowsPerPage },
      application,
      setApplication,
      status,
      setStatus,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      setLanguageChange,
      language,
      category,
      setCategoryChange,
      searchHandler,
      isDateExplicit,
      setDateExplicit,
      resetDates,
    },
  } = useStore();

  const recommendedOption = getCategoryCode.find(
    (c) => c.key === "Recommended"
  );
  const aiReviewCategoryOptions = recommendedOption
    ? [defaultAllCategory, recommendedOption]
    : [defaultAllCategory];

  const [btnOpen, setBtnOpen] = useState(false);
  const [cookies, setCookie] = useCookies();
  const expires = moment().add("100", "y").toDate();
  const gCookies = new Cookies();
  const getCookie = (item: string) => {
    return gCookies.get(item);
  };

  const selectProps: SelectProps = {
    width: 122,
    name: "Status",
    value: status,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setStatus(event.target.value);
    },
    defaultValue: "All",
    list: [
      { label: "Preparing", value: "Preparing" },
      { label: "Rejected", value: "Rejected" },
      { label: "Published", value: "Published" },
      { label: "Blind", value: "Blind" },
      { label: "All", value: "All" },
    ],
  };

  const selectApplicationProps: SelectProps = {
    width: 122,
    name: "Application",
    value: application,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setApplication(event.target.value);
    },
    defaultValue: "all",
    list: [
      { label: "All", value: "all" },
      { label: "KineMaster", value: "kinemaster" },
      { label: "Spring", value: "spring" },
    ],
  };

  // DateRangePicker는 마운트 시점에도 setStartDate/setEndDate를 현재 값으로 한 번
  // 호출한다(no-op). 값이 실제로 바뀐 경우에만 "명시적 날짜 지정"으로 표시해,
  // 사용자가 날짜를 건드리지 않으면 기본값(오늘 0시)이 유지되도록 한다.
  const handleStartDateChange = (date: Date) => {
    if (!dayjs(date).isSame(startDate)) setDateExplicit(true);
    setStartDate(date);
  };
  const handleEndDateChange = (date: Date) => {
    if (!dayjs(date).isSame(endDate)) setDateExplicit(true);
    setEndDate(date);
  };

  const rangeDateProps = {
    startDate,
    endDate,
    setStartDate: handleStartDateChange,
    setEndDate: handleEndDateChange,
  };
  const onChangeLang = (event: any, value: any) => {
    if (value) {
      setLanguageChange(value);
    }
  };

  const onChangeCategory = (event: any, value: any) => {
    if (value) {
      setCategoryChange(value);
    }
  };

  const handleButton = () => {
    // ai_page는 저장하지 않는다 — AI Review는 항상 page=0으로 진입한다.
    // (저장하면 데이터가 줄었을 때 범위 밖 page가 되어 DataGrid 보정 → 중복 fetch)
    setCookie("ai_rowsPerPage", rowsPerPage, { path: "/", expires });
    setCookie("ai_startDate", startDate, { path: "/", expires });
    setCookie("ai_endDate", endDate, { path: "/", expires });
    // 명시적 날짜 여부를 함께 저장한다. "1"이면 재진입/새로고침 시 이 날짜로
    // 복원된다. 사용자가 날짜를 건드리지 않았으면 "0"이 저장되어 기본값(오늘 0시)이
    // 유지된다.
    setCookie("ai_dateExplicit", isDateExplicit ? "1" : "0", {
      path: "/",
      expires,
    });
    setCookie("ai_application", application, { path: "/", expires });
    setCookie("ai_status", status, { path: "/", expires });
    setCookie("ai_language", JSON.stringify(language), { path: "/", expires });
    setCookie("ai_category", JSON.stringify(category), { path: "/", expires });

    // setRows()를 직접 부르면 직전에 페이지를 넘겨둔 상태(page>0)에서 필터가
    // 바뀌어 결과가 줄었을 때 범위 밖 page로 조회 → DataGrid 보정 → 재조회가
    // 발생한다. searchHandler가 page=0으로 리셋한 뒤 조회하므로 그것을 쓴다.
    searchHandler();
    setBtnOpen(false);
  };

  const handleClose = () => {
    setApplication(
      getCookie("ai_application") ? getCookie("ai_application") : "all"
    );
    setStatus(getCookie("ai_status") ? getCookie("ai_status") : "All");

    // 날짜/명시 플래그는 적용된 상태(쿠키 기준)로 되돌린다. 다이얼로그에서 날짜를
    // 건드렸다가 Cancel한 경우 isDateExplicit도 함께 원복된다.
    resetDates();

    setLanguageChange(
      getCookie("ai_language") ? getCookie("ai_language") : defaultAllCode
    );
    setCategoryChange(
      getCookie("ai_category") ? getCookie("ai_category") : defaultAllCategory
    );

    setBtnOpen(false);
  };

  /**
   * 필터 다이얼로그 안에서 Enter 를 누르면 OK(handleButton) 와 동일하게 동작시킨다.
   *
   * bubble 이 아니라 capture 단계에서 처리한다. bubble 로 두면 필터 값을 바꾼 뒤
   * Select 에 포커스가 남아 있을 때 MUI Select 가 Enter 를 먼저 가로채 메뉴를 다시
   * 열고 preventDefault 해버려서 검색이 실행되지 않는다. capture 에서 먼저 잡고
   * stopPropagation 으로 그 동작을 막는다.
   *
   * 단, 아래 경우는 Enter 가 다른 의미를 가지므로 그대로 흘려보낸다.
   *  - IME(한글 등) 조합 확정용 Enter
   *  - 드롭다운(Select 메뉴 / Autocomplete 목록)이 열려 있을 때 → 항목 선택
   *  - 버튼에 포커스가 있을 때 → 그 버튼의 클릭 (Cancel / OK)
   *  - 달력의 날짜 직접 입력 칸 → 입력값 확정
   */
  const handleDialogKeyDownCapture = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    if (event.nativeEvent.isComposing) return;

    const target = event.target as HTMLElement;
    if (document.querySelector(".MuiPopover-root, .MuiAutocomplete-popper")) {
      return;
    }
    if (target.closest("button")) return;
    if (target.closest(".rdrDateDisplayWrapper")) return;

    event.preventDefault();
    event.stopPropagation();
    handleButton();
  };

  return (
    <>
      <SecondContainer>
        <FilterAltIcon
          onClick={() => {
            setBtnOpen(true);
          }}
          sx={{ cursor: "pointer", color: "#6750a4" }}
        />
        <SecondBox
          sx={{
            display: "flex",
            flexDirection: "column",
            // width:100% 는 아이콘과 합쳐 컨테이너를 넘긴다. 남은 공간만 차지하게 한다.
            flex: 1,
            minWidth: 0,
          }}
        >
          <ThirdBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              flexWrap: "wrap",
              pt: "10px",
            }}
          >
            <FilterItem label="Application">
              {application === "kinemaster"
                ? "KineMaster"
                : application === "spring"
                ? "Spring"
                : "All"}
            </FilterItem>
            <FilterItem label="Status">{status}</FilterItem>
            <FilterItem label="Date">
              {dayjs(startDate).format("YYYY-MM-DD") +
                " ~ " +
                dayjs(endDate).format("YYYY-MM-DD")}
            </FilterItem>
            <FilterItem label="Language">{language.name}</FilterItem>
            <FilterItem label="Category">{category.key}</FilterItem>
          </ThirdBox>
        </SecondBox>
      </SecondContainer>

      <Dialog
        open={btnOpen}
        onClose={handleClose}
        onKeyDownCapture={handleDialogKeyDownCapture}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "900px",
            minWidth: "900px",
            maxHeight: "650px",
            minHeight: "650px",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Select {...selectApplicationProps} />
            <Select {...selectProps} />
            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-lang"
              value={language}
              options={[defaultAllCode].concat(getLanguageCode)}
              onChange={onChangeLang}
              getOptionLabel={(option: any) => {
                if (option.name === "All") {
                  return `All`;
                } else {
                  return `${option.name} (${option.value})`;
                }
              }}
              renderInput={(params) => {
                return <TextField {...params} label="Language" />;
              }}
              isOptionEqualToValue={(option: any, value: any) =>
                option.name === value.name && option.value === value.value
              }
              sx={{
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: 240,
                  mr: "4px",
                },
              }}
            />
            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-category"
              value={category}
              options={aiReviewCategoryOptions}
              onChange={onChangeCategory}
              getOptionLabel={(option: any) => {
                return option.key === "All" ? "All" : option.key;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Category" />
              )}
              isOptionEqualToValue={(option: any, value: any) =>
                option.key === value.key
              }
              sx={{
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: 180,
                  mr: "4px",
                },
              }}
            />
          </Box>

          <DialogContent
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              pl: 0,
              pt: 1,
            }}
          >
            <DateRangePicker {...rangeDateProps} />
          </DialogContent>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleButton}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default observer(Handler);
